import * as React from 'react';
import { AppContext } from '../../../App';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';

import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { usePackageUpdate } from '../../hooks/packages/usePackageUpdate';
import { PackageStatuses } from '../../../types/packager-types';
import { AxiosError } from 'axios';
import { extractApiError } from '../../../utils/extractApiError';
export interface IPackageVerifyProps {
  packageId: string;
  packageStatus: PackageStatuses;
}

const PackageVerify: React.FC<IPackageVerifyProps> = ({ packageId, packageStatus }) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const theme = useTheme();

  const mutationPackageUpdate = usePackageUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });
      queryClient.invalidateQueries({
        queryKey: ['packages']
      });
      // The detail query gates entry edits in PackagerDocuments.
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package verification status updated!',
      });
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to update verification status'),
      });
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const handleUpdate = () => {
    mutationPackageUpdate.mutate({
      packageId: packageId!,
      data: {
        status:
          packageStatus === PackageStatuses.VERIFIED
            ? PackageStatuses.PROCESSING_SUCCESSFUL
            : PackageStatuses.VERIFIED,
      },
    });
    setOpen(false);
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={packageStatus === PackageStatuses.VERIFIED ? 'Un-verify Package' : 'Verify Package'}
        disableFocusListener
        disableTouchListener
      >
        {packageStatus !== PackageStatuses.VERIFIED ? (
          <IconButton
            data-cy={`btn-verify-package-${packageId}`}
            onClick={!mutationPackageUpdate.isPending ? handleClickOpen : undefined}
            disabled={mutationPackageUpdate.isPending}
            sx={{
              color: theme.palette.custom.lightGreen,
              '&:hover': {
                backgroundColor: theme.palette.custom.lightGreen + '20', // 20% opacity for hover
              },
            }}
          >
            <DoneAllIcon fontSize="medium" />
          </IconButton>
        ) : (
          <IconButton
            data-cy={`btn-unverify-package-${packageId}`}
            onClick={!mutationPackageUpdate.isPending ? handleClickOpen : undefined}
            disabled={mutationPackageUpdate.isPending}
            sx={{
              color: theme.palette.custom.lightGreen,
              '&:hover': {
                backgroundColor: theme.palette.custom.lightGreen + '20', // 20% opacity for hover
              },
            }}
          >
            <RemoveDoneIcon fontSize="medium" />
          </IconButton>
        )}
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={mutationPackageUpdate.isPending}
        cypressId="dialog-package-verify"
      >
        <DialogTitleGlynt
          title="Confirmation"
          closeModal={handleCloseConfirmation}
          loading={mutationPackageUpdate.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            Are you sure you want to{' '}
            {packageStatus === PackageStatuses.VERIFIED ? 'un-verify' : 'verify'} this package?
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleUpdate}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-package-verify-modal-cancel"
          submitCypressId="btn-package-verify-modal-yes"
          clearCypressId=""
          submitButtonText="Yes"
          submitLoading={mutationPackageUpdate.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageVerify;
